import { type CSSProperties, type ReactElement } from "react";
import { Check } from "lucide-react";
import type { NotificationItem } from "@playbookai/types";
import {
  Badge,
  Button,
  Link,
  StatusDot,
  enterDelay,
  isSafeNotificationHref,
  useListPresence,
} from "@playbookai/ui";
import type { DateTimeFormatter } from "../settings/format-date-time.js";
import { moduleDotProps } from "../modules/launcher-entries.js";
import {
  categoryIcon,
  categoryOf,
  notificationModuleLabel,
  TONE_ICON,
} from "./notification-presentation.js";

interface NotificationFeedListProps {
  readonly groups: readonly {
    readonly key: string;
    readonly label: string;
    readonly items: readonly NotificationItem[];
  }[];
  /**
   * Identity of the QUESTION these rows answer — the filters, the search and
   * the page.
   *
   * A row leaves on the exit grade only when it was removed FROM THE SET the
   * reader is looking at (ADR-0091). Changing the filter replaces the set
   * instead, and retaining the old answer while the new one arrives would show
   * two results at once. Keying the groups on this remounts them, so a new
   * question simply arrives.
   */
  readonly viewKey: string;
  readonly formatter: DateTimeFormatter;
  readonly onMarkRead: (id: string) => void;
  readonly onOpen: (href: string, title: string, eventType?: string) => void;
  /**
   * The address a row's link carries, for a new browser tab or a copied link.
   * A click still opens the row's own href through `onOpen`.
   */
  readonly resolveHref: (href: string, eventType?: string) => string;
}

export function NotificationFeedList({
  groups,
  viewKey,
  formatter,
  onMarkRead,
  onOpen,
  resolveHref,
}: NotificationFeedListProps): ReactElement {
  return (
    <div className="flex flex-col gap-6">
      {/* Three day groups at most, so the stagger is short and deliberate
          (ADR-0091) — Today lands first and Earlier follows it. */}
      {groups.map((group, index) => (
        <section
          key={`${viewKey}|${group.key}`}
          className="pb-enter-row"
          // The cast is for the custom property `pb-enter-row` reads; React's
          // CSSProperties has no member for one.
          style={enterDelay(index, 40) as CSSProperties}
          aria-labelledby={`notification-day-${group.key}`}
        >
          <h2
            id={`notification-day-${group.key}`}
            className="mb-2 px-1 text-body-small font-semibold text-pb-text-secondary"
          >
            {group.label}
          </h2>
          <NotificationFeedGroup
            items={group.items}
            formatter={formatter}
            onMarkRead={onMarkRead}
            onOpen={onOpen}
            resolveHref={resolveHref}
          />
        </section>
      ))}
    </div>
  );
}

/* One day's rows. Marking a notification read REMOVES it from the unread view,
   which is a removal the reader caused and is looking straight at: without a
   presence list the rows below it jump up under the pointer (ADR-0091). The
   leaving row keeps its slot until it has collapsed, and is scenery while it
   does — hidden from assistive tech, out of the tab order, inert. Not
   virtualized, so an arriving row may also carry the entrance grade. */
function NotificationFeedGroup({
  items,
  formatter,
  onMarkRead,
  onOpen,
  resolveHref,
}: {
  readonly items: readonly NotificationItem[];
  readonly formatter: DateTimeFormatter;
  readonly onMarkRead: (id: string) => void;
  readonly onOpen: (href: string, title: string, eventType?: string) => void;
  readonly resolveHref: (href: string, eventType?: string) => string;
}): ReactElement {
  const { rows } = useListPresence(items, (item) => item.id);
  return (
    /* Kept hand-built: Card paints its 1px inset top hairline (pb-hairline)
       on a flat default card, a look change no PR 3 row approves. */
    <ul className="overflow-hidden rounded-xl border border-pb-border/80 bg-pb-card divide-y divide-pb-border/60">
      {/* ONE shape for every row, open or leaving (ADR-0091). Rendering the
          collapse class only on the row that is going would apply the grid and
          its closing state in the same frame, and a transition with no earlier
          value to start from simply snaps. The inner box is what clips and what
          carries the arrival. */}
      {rows.map((row) => (
        <li
          key={row.key}
          /* Two-way: a row that comes back while it was leaving must become
             reachable again. Imperative because React 18 serialises
             `inert={false}` as the truthy string "false". */
          ref={(node) => node?.toggleAttribute("inert", !row.open)}
          className="pb-exit-row"
          data-motion-state={row.open ? "open" : "closing"}
          {...(row.open ? {} : { "aria-hidden": "true" as const })}
        >
          <div className={row.open ? "pb-enter-row" : undefined}>
            <NotificationFeedRow
              item={row.item}
              formatter={formatter}
              onMarkRead={onMarkRead}
              onOpen={onOpen}
              resolveHref={resolveHref}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function NotificationFeedRow({
  item,
  formatter,
  onMarkRead,
  onOpen,
  resolveHref,
}: {
  readonly item: NotificationItem;
  readonly formatter: DateTimeFormatter;
  readonly onMarkRead: (id: string) => void;
  readonly onOpen: (href: string, title: string, eventType?: string) => void;
  readonly resolveHref: (href: string, eventType?: string) => string;
}): ReactElement {
  const href = isSafeNotificationHref(item.href) ? item.href : null;
  /* The module dot the tab strip carries (ADR-0092); a platform or unknown
     module id has none. */
  const moduleDot =
    item.moduleId == null ? undefined : moduleDotProps(item.moduleId);
  const Icon = categoryIcon(categoryOf(item));
  const body = (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <p
          className={`text-body font-medium ${item.unread ? "text-pb-text" : "text-pb-text-secondary"}`}
        >
          {item.title}
        </p>
        {item.unread ? <span className="sr-only">Unread</span> : null}
      </div>
      <p className="mt-1 text-body-small leading-relaxed text-pb-text-secondary">
        {item.description}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {/* Kept as today's untoned outline: Badge tone="neutral" would draw
            the pb-border hairline instead of today's muted currentColor one,
            a look change no PR 3 row approves. */}
        <Badge variant="outline" className="text-pb-text-muted">
          {moduleDot ? <StatusDot size="xs" {...moduleDot} /> : null}
          {notificationModuleLabel(item)}
        </Badge>
        <time dateTime={item.ts} className="text-dense-1 text-pb-text-muted">
          {formatter.relative(item.ts)}
        </time>
      </div>
    </div>
  );

  return (
    /* A div, not the list item: the `<li>` above is the presence wrapper, which
       is what collapses when this row is marked read (ADR-0091). Kept off
       ListItem: the row holds a link AND a mark-read button, and ListItem is
       one button with no unread edge. */
    <div
      /* Unread is the info cyan edge and wash (OK-88, d12): accent marks the
         region's one commit, not a state. */
      className={`group flex items-start gap-3 border-s-[3px] px-4 py-4 transition-colors motion-reduce:transition-none hover:pb-overlay-paint active:pb-overlay-paint-strong ${item.unread ? "border-s-pb-cyan bg-pb-cyan/5" : "border-s-transparent"}`}
    >
      {/* Kept: IconTile has no 36px step (24/32/40/48) and draws a border
          where this tile draws a ring; no PR 3 look row approves either. */}
      <span
        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ${TONE_ICON[item.tone]}`}
        aria-hidden
      >
        <Icon className="h-4 w-4" />
      </span>
      {href === null ? (
        body
      ) : (
        /* The row's text is a plain block link (OK-52): it keeps the row's
           colours and takes the design-system link's focus treatment. */
        <Link
          href={resolveHref(href, item.eventType)}
          variant="plain"
          display="block"
          className="min-w-0 flex-1"
          onClick={(event) => {
            onMarkRead(item.id);
            if (
              event.button !== 0 ||
              event.metaKey ||
              event.ctrlKey ||
              event.shiftKey ||
              event.altKey
            ) {
              return;
            }
            event.preventDefault();
            onOpen(href, item.title, item.eventType);
          }}
        >
          {body}
        </Link>
      )}
      {item.unread ? (
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label={`Mark read — ${item.title}`}
          className="shrink-0 opacity-70 group-hover:opacity-100"
          onClick={() => onMarkRead(item.id)}
        >
          <Check className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
}
